import { doc, type DocumentData, type DocumentReference } from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import type { UserNotificationRefs } from '@domain/models/user-notifications'

function getDocRef(
  collectionPath: string,
  id: string,
): DocumentReference<DocumentData> {
  if (!firestore) {
    throw new Error('Firestore is not initialized.')
  }
  return doc(firestore, collectionPath, id)
}

function isReferenceLike(value: unknown): value is DocumentReference<DocumentData> {
  if (!value || typeof value !== 'object') {
    return false
  }
  if (!('id' in value) || !('path' in value)) {
    return false
  }

  const maybeRef = value as { id?: unknown; path?: unknown }
  return typeof maybeRef.id === 'string' && typeof maybeRef.path === 'string'
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function stripNullish(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null),
  )
}

function toOptionalRef(
  collectionPath: string,
  value: string | undefined,
): DocumentReference<DocumentData> | undefined {
  const trimmedValue = value?.trim()
  if (!trimmedValue) {
    return undefined
  }
  return getDocRef(collectionPath, trimmedValue)
}

export class UserNotificationRefsConverter {
  public static fromFirestore(data: unknown): UserNotificationRefs | undefined {
    if (!data || typeof data !== 'object') {
      return undefined
    }

    const rawData = data as Record<string, unknown>
    const refs: UserNotificationRefs = {
      commentReply: refToId(rawData['comment_reply']),
      comment: refToId(rawData['comment']),
      solution: refToId(rawData['solution']),
      activity: refToId(rawData['activity']),
      meetup: refToId(rawData['meetup']),
      group: refToId(rawData['group']),
      routine: refToId(rawData['routine']),
    }

    if (Object.values(refs).every((value) => value === undefined)) {
      return undefined
    }

    return refs
  }

  public static toFirestore(refs: UserNotificationRefs): Record<string, unknown> {
    return stripNullish({
      comment_reply: toOptionalRef('activities', refs.commentReply),
      comment: toOptionalRef('activities', refs.comment),
      solution: toOptionalRef('what_works', refs.solution),
      activity: toOptionalRef('activities', refs.activity),
      meetup: toOptionalRef('meetups', refs.meetup),
      group: toOptionalRef('groups', refs.group),
      routine: toOptionalRef('user_routine', refs.routine),
    })
  }
}
